/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, April 11th 2022, 4:47:31 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { APISERVICEPATHS, useAPIFetchList, useAPIForm } from "lib/api";
import {
  DocumentTemplate,
  DocumentTemplateValidationSchema,
  Organization,
  NumberingSequence,
  ProcessDefinition,
} from "lib/schemas";
import { useGlobalScope } from "core/providers";
import { PrimaryKeyValue } from "lib/types";

/* DocumentTemplate API */

const documentTemplateAPIServicePath = APISERVICEPATHS.documentTemplates;

export const useAPIListDocumentTemplates = (processDefinitionId: ProcessDefinition["id"]) =>
  useAPIFetchList<DocumentTemplate>({
    apiServicePath: documentTemplateAPIServicePath,
    apiQueryParams: { process_definition: processDefinitionId },
    makeRequestOnInitialMount: true,
  });

export const useDocumentTemplateAPIForm = (documentTemplateId: PrimaryKeyValue<DocumentTemplate>) => {
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const newDocumentTemplateDefault = getDefaultDocumentTemplate(selectedOrganization, null);

  const [documentHasValidityPeriod, setDocumentHasValidityPeriod] = React.useState<boolean>();

  const handleAPIResponse = (documentTemplate: DocumentTemplate) =>
    setDocumentHasValidityPeriod(documentTemplate.valid_for > 0);

  const useDocumentTemplateAPIFormReturn = useAPIForm({
    apiServicePath: documentTemplateAPIServicePath,
    apiFormDataValidationSchema: DocumentTemplateValidationSchema,
    apiFormDefaultValues: newDocumentTemplateDefault,
    apiInitialFetchId: documentTemplateId,
    primaryKey: "id",
    setAPIFormResponse: handleAPIResponse,
  });

  const handleChangeDocumentHasValidity = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDocumentHasValidityPeriod(event.target.checked);
    if (!event.target.checked) useDocumentTemplateAPIFormReturn.useFormReturn.setValue("valid_for", 0);
  };

  return { useDocumentTemplateAPIFormReturn, documentHasValidityPeriod, handleChangeDocumentHasValidity };
};

const getDefaultDocumentTemplate = (
  organization: Organization,
  defaultNumberingSequence: PrimaryKeyValue<NumberingSequence> & number,
  optionalProperties?: Partial<DocumentTemplate>
): DocumentTemplate => {
  return {
    artifact_org: organization.id,
    sequence: defaultNumberingSequence,
    title: "",
    document_type: "other",
    layout: null,
    valid_for: 0,
    ...optionalProperties,
  };
};
